import type { IAgentProfileSummaryView } from '@commenda/api';
import { AgentProfileHelper } from '@commenda/utils';

import { ComposerPickerMenuHelper } from './composer-picker-menu-helper';

const DATABASE_PROFILE_PREFIX = 'user:';

/** Rules for the composer's agent picker and the chip that replaces it in an open conversation. */
export class ComposerProfileMenuHelper {
  /** A sentinel, not a profile key: an unset conversation runs the default, so "General" is the
   * absence of a choice. */
  static readonly GENERAL_VALUE = 'general';

  static readonly GENERAL_LABEL = 'General';

  static readonly GENERAL_DESCRIPTION =
    'Ask anything across your entities, filings and documents';

  static readonly AGENTS_GROUP_LABEL = 'Agents';

  /** The pill both the picker and the static chip render as, so the two cannot drift. */
  static readonly TRIGGER_CLASS_NAMES = `${ComposerPickerMenuHelper.PILL_CLASS_NAMES} max-w-[200px] px-lg`;

  /** An allowlist by origin, because every platform key is machinery and a blacklist would silently
   * start offering the next one added. */
  static selectableProfiles(
    profiles: readonly IAgentProfileSummaryView[]
  ): IAgentProfileSummaryView[] {
    return profiles.filter((profile) =>
      profile.profileKey.startsWith(DATABASE_PROFILE_PREFIX)
    );
  }

  /** A failed list reads differently from an empty one: the surface is role-gated and answers 403. */
  static getEmptyText(isLoading: boolean, error: unknown): string {
    if (error) return "Couldn't load agents.";
    if (isLoading) return 'Loading agents...';
    return 'No agents available.';
  }

  static toProfileKey(value: string): string | null {
    return value === ComposerProfileMenuHelper.GENERAL_VALUE ? null : value;
  }

  /** An unknown key reads as "General" rather than as a raw key: the list may not have loaded yet. */
  static getProfileName(
    profiles: readonly IAgentProfileSummaryView[],
    profileKey: string | null
  ): string {
    const matched = profiles.find(
      (profile) => profile.profileKey === profileKey
    );
    return profileKey === null || matched === undefined
      ? ComposerProfileMenuHelper.GENERAL_LABEL
      : AgentProfileHelper.getDisplayName(matched);
  }
}
